import {
  type Query,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { produce } from 'immer'
import { type CacheUpdateFn } from './buildQueryHelpers'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * The ids in `filter[filterName]` of a collection key built by
 * `buildQueryHelpers` for `operationId`, or `undefined` for any other key.
 */
export const filterIdsOfKey = (
  queryKey: QueryKey,
  operationId: string,
  filterName: string
): unknown[] | undefined => {
  if (queryKey[2] !== operationId) return undefined
  const params = queryKey[3]
  const filter = isRecord(params) ? params.filter : undefined
  const ids = isRecord(filter) ? filter[filterName] : undefined
  return Array.isArray(ids) ? ids : undefined
}

const latestRefreshes = new WeakMap<QueryClient, Map<string, number>>()

/** Returns a check that is true until a newer refresh of `refreshKey` starts. */
const startRefresh = (queryClient: QueryClient, refreshKey: string) => {
  const latest = latestRefreshes.get(queryClient) ?? new Map<string, number>()
  latestRefreshes.set(queryClient, latest)
  const sequence = (latest.get(refreshKey) ?? 0) + 1
  latest.set(refreshKey, sequence)
  return () => latest.get(refreshKey) === sequence
}

/**
 * Re-reads one entity into every cached collection document whose key filters
 * by it, for the twin v2/v3 workflow summary caches. Short-circuits if no
 * document covers the entity. If the read fails, the documents are
 * invalidated instead of the promise failing.
 */
export const refreshCachedEntityDocuments = async <TDocument>({
  entityId,
  filterName,
  operationId,
  queryClient,
  read,
  replace,
  resourceKey,
}: {
  entityId: string | number
  filterName: string
  operationId: string
  queryClient: QueryClient
  read: () => Promise<TDocument>
  replace: (incoming: TDocument) => CacheUpdateFn<TDocument>
  resourceKey: string
}): Promise<void> => {
  const filters = {
    queryKey: [resourceKey],
    predicate: ({ queryKey }: Query) =>
      filterIdsOfKey(queryKey, operationId, filterName)?.includes(entityId) ??
      false,
  }
  if (!queryClient.getQueryCache().findAll(filters).length) return

  // Two quick writes start two refreshes, and their reads can end in either
  // order. Only the newest one writes.
  const isLatest = startRefresh(
    queryClient,
    `${operationId}:${filterName}:${entityId}`
  )
  let incoming: TDocument
  try {
    incoming = await read()
  } catch {
    await queryClient.invalidateQueries(filters)
    return
  }
  if (!isLatest()) return

  // A read that started before the change would end with old data, so it is
  // cancelled. Documents with data then get the write. A document that was
  // reading (its first read, or a background refetch whose other entities
  // also need fresh data) reads again; a refetch alone would join the old
  // read, not replace it.
  const queries = queryClient.getQueryCache().findAll(filters)
  const interrupted = new Set(
    queries.filter(
      ({ state }) => state.data === undefined || state.fetchStatus !== 'idle'
    )
  )
  await queryClient.cancelQueries(filters)
  const update = replace(incoming)
  queries.forEach(({ queryKey }) =>
    queryClient.setQueryData<TDocument | undefined>(queryKey, (document) =>
      document ? produce(document, update) : document
    )
  )
  void queryClient.refetchQueries({
    ...filters,
    predicate: (query) => interrupted.has(query),
  })
}
